'use client';

import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css'; // 引入video.js的样式
import Player from 'video.js/dist/types/player';
import Image from 'next/image';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

function VideoPlayer({
    hlsUrl,
    cover,
}: Readonly<{ hlsUrl: string; cover: string }>) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<Player | null>(null);
    const [showPlayButton, setShowPlayButton] = useState(true); // 状态控制播放按钮显示
    const [posterLoaded, setPosterLoaded] = useState(false);

    useEffect(() => {
        setPosterLoaded(false);

        const posterUrl = getStaticAssetUrl(cover);
        const image = new window.Image();
        image.src = posterUrl;

        if (image.complete && image.naturalWidth > 0) {
            setPosterLoaded(true);
            return;
        }

        const handleLoad = () => setPosterLoaded(true);
        const handleError = () => setPosterLoaded(true);

        image.addEventListener('load', handleLoad);
        image.addEventListener('error', handleError);

        return () => {
            image.removeEventListener('load', handleLoad);
            image.removeEventListener('error', handleError);
        };
    }, [cover]);

    // 初始化和清理视频播放器
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    !playerRef.current &&
                    videoRef.current
                ) {
                    // 确保 videoRef.current 非空
                    playerRef.current = videojs(videoRef.current, {
                        sources: [
                            { src: hlsUrl, type: 'application/x-mpegURL' },
                        ],
                        autoplay: false,
                        muted: true,
                        controls: false,
                        responsive: true,
                        fluid: true,
                        loop: true,
                    });

                    playerRef.current.on('play', () => {
                        setShowPlayButton(false);
                    });
                    playerRef.current.on('pause', () => {
                        setShowPlayButton(true);
                    });
                }
            },
            { threshold: 0.5 },
        );

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        return () => {
            if (observer && videoRef.current) {
                observer.unobserve(videoRef.current);
            }
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [hlsUrl]);

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault(); // 阻止默认右键菜单
    };

    return (
        <div className="relative h-full w-full">
            {!posterLoaded && (
                <div className="before:via-white/8 absolute inset-0 z-20 overflow-hidden bg-[#18181D] before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.8s_infinite] before:bg-gradient-to-r before:from-transparent before:to-transparent" />
            )}
            <div data-vjs-player className="h-full w-full">
                <video
                    ref={videoRef}
                    className="video-js"
                    playsInline
                    onContextMenu={handleContextMenu}
                    poster={getStaticAssetUrl(cover)}
                />
            </div>

            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
                {posterLoaded && showPlayButton && (
                    <button
                        onClick={() => playerRef.current?.play()}
                        className="h-[72px] w-[72px] opacity-60 hover:opacity-100 active:opacity-100"
                    >
                        <Image
                            src={getStaticAssetUrl('/icon-video-play.webp')}
                            width={72}
                            height={72}
                            alt="icon-video-play"
                        />
                    </button>
                )}
            </div>
        </div>
    );
}

export default VideoPlayer;
